import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import QRCode from 'react-qr-code';
import { Plus, Printer, Lock, LayoutGrid, Loader, Trash2, Download } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth'; // 👈 NEW: Imported Auth Hook
import html2canvas from 'html2canvas';

const TableManager = () => {
    const { register, handleSubmit, reset } = useForm();
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth(); // 👈 NEW: Get active branch ID

    const { data: tables = [], isLoading } = useQuery({
        // 👈 NEW: Added activeBranchId to trigger re-fetch on branch switch
        queryKey: ['tables-list', activeBranchId],
        queryFn: async () => {
            const res = await axiosSecure.get('/tables');
            return res.data?.data || [];
        },
        enabled: !!activeBranchId // 👈 NEW: Wait for branch to be selected
    });

    const onSubmit = async (data) => {
        try {
            const res = await axiosSecure.post('/table/add', {
                tableNo: Number(data.tableNo),
                passcode: data.passcode
            });
            if (res.data.success) {
                Swal.fire({ icon: 'success', title: 'Table Added', timer: 1500, showConfirmButton: false, background: '#1a103c', color: '#fff' });
                reset();
                // 👈 NEW: Invalidate specifically for the active branch
                queryClient.invalidateQueries(['tables-list', activeBranchId]);
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to add table' });
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Delete Table?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, delete',
            background: '#1a103c',
            color: '#fff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await axiosSecure.delete(`/table/delete/${id}`);
                    if (res.data.success) {
                        Swal.fire({ title: 'Deleted!', icon: 'success', background: '#1a103c', color: '#fff', timer: 1000, showConfirmButton: false });
                        // 👈 NEW: Invalidate specifically for the active branch
                        queryClient.invalidateQueries(['tables-list', activeBranchId]);
                    }
                } catch (error) {
                    Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete table' });
                }
            }
        });
    };

    const downloadQRCode = async (tableNo) => {
        const element = document.getElementById(`qr-card-${tableNo}`);
        
        const canvas = await html2canvas(element, { 
            backgroundColor: null, 
            scale: 3, 
            useCORS: true,
            allowTaint: true,
            removeContainer: true,
        });
        
        const image = canvas.toDataURL("image/png", 1.0);
        const link = document.createElement('a');
        link.href = image;
        link.download = `Table-${tableNo}-QR.png`;
        link.click();
    };

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Tables...</div>;
    if (isLoading) return <div className="text-white text-center mt-20"><Loader className="animate-spin mx-auto" /> Loading Tables...</div>;

    return (
        <div className="space-y-8">
            {/* --- HEADER --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <LayoutGrid className="text-pink-500" /> Table Management
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Create tables and print QR codes for customers.</p>
                </div>
                <button onClick={() => window.print()} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold flex items-center gap-2 transition-colors print:hidden">
                    <Printer size={18} /> Print All QRs
                </button>
            </div>

            {/* --- ADD NEW TABLE FORM --- */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-xl print:hidden">
                <h3 className="text-lg font-bold text-white mb-6">Add New Table</h3>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="w-full md:w-1/3 space-y-2">
                        <label className="text-sm font-bold text-slate-300 ml-1">Table Number</label>
                        <input {...register("tableNo", { required: true })} type="number" placeholder="e.g. 1" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500" />
                    </div>
                    <div className="w-full md:w-1/3 space-y-2">
                        <label className="text-sm font-bold text-slate-300 ml-1">Secret Passcode</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                            <input {...register("passcode", { required: true })} type="text" placeholder="e.g. 1234" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-pink-500" />
                        </div>
                    </div>
                    <button className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-xl text-white font-bold hover:shadow-lg hover:shadow-pink-500/30 transition-all flex items-center justify-center gap-2">
                        <Plus size={20} /> Add Table
                    </button>
                </form>
            </div>

            {/* --- QR GRID --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 print:grid-cols-3">
                {tables.map((table) => (
                    <div 
                        key={table._id} 
                        className="group relative p-0 transition-all hover:scale-[1.02] break-inside-avoid"
                    >
                        {/* THE CARD CONTAINER - We download THIS ID to ensure rounding is perfect */}
                        <div 
                            id={`qr-card-${table.tableNo}`} 
                            className="bg-white p-6 rounded-[2.5rem] flex flex-col items-center text-center border-4 border-slate-900 h-full relative overflow-hidden"
                        >
                            
                            {/* Hover Actions */}
                            <div 
                                data-html2canvas-ignore="true"
                                className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 print:hidden"
                            >
                                <button onClick={() => downloadQRCode(table.tableNo)} className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg hover:bg-blue-700 transition-colors">
                                    <Download size={18} />
                                </button>
                                <button onClick={() => handleDelete(table._id)} className="p-3 bg-red-500 text-white rounded-2xl shadow-lg hover:bg-red-600 transition-colors">
                                    <Trash2 size={18} />
                                </button>
                            </div>

                            {/* Header */}
                            <div className="mb-6">
                                <span className="px-3 py-1 bg-pink-100 text-pink-600 rounded-full text-[10px] font-black uppercase tracking-widest">Premium Table</span>
                                <h3 className="text-4xl font-black text-[#1a103c] mt-2 italic">#{table.tableNo}</h3>
                                <div className="h-1.5 w-16 bg-gradient-to-r from-pink-500 to-purple-500 mx-auto mt-2 rounded-full"></div>
                            </div>

                            {/* QR Code Section */}
                            <div className="p-4 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200 mb-6">
                                <QRCode 
                                    value={`${window.location.origin}/menu/table/${table.tableNo}`} 
                                    size={150} 
                                    level="H" 
                                />
                            </div>

                            {/* Passcode Footer */}
                            <div className="w-full bg-slate-900 py-4 rounded-[1.5rem] shadow-xl">
                                <p className="text-[10px] text-slate-400 uppercase font-bold tracking-[0.2em] mb-1">Entry Passcode</p>
                                <p className="text-3xl font-mono font-black text-white tracking-[0.3em]">{table.passcode}</p>
                            </div>

                            <p className="mt-5 text-[11px] text-slate-500 font-bold uppercase tracking-tight">Scan to explore menu</p>
                        </div>
                    </div>
                ))}
                
                {tables.length === 0 && (
                     <div className="col-span-full py-20 text-center text-slate-500 flex flex-col items-center print:hidden">
                         <LayoutGrid size={48} className="mb-4 opacity-20" />
                         <p>No tables configured for this branch yet.</p>
                     </div>
                )}
            </div>

            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .print\\:hidden { display: none !important; }
                    .print\\:grid-cols-3 {
                        display: grid !important;
                        grid-template-columns: repeat(3, 1fr) !important;
                        visibility: visible !important;
                        position: absolute; left: 0; top: 0; width: 100%; gap: 30px;
                    }
                    .break-inside-avoid { visibility: visible !important; page-break-inside: avoid; }
                    .break-inside-avoid * { visibility: visible !important; }
                }
            `}</style>
        </div>
    );
};

export default TableManager;